// Lightweight i18n dictionary for EduRepository (English + Hindi)
// Usage: const { t, lang, setLang } = useI18n();
import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const STORAGE_KEY = 'edurepo_lang';

const DICT = {
  en: {
    // Nav / common
    home: 'Home',
    library: 'Library',
    analytics: 'Analytics',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',
    dashboard: 'Dashboard',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    // Dashboard
    yourLessonPlans: 'Your Lesson Plans',
    dashboardSubtitle: 'Create and manage all your lesson plans',
    newLessonPlan: 'New Lesson Plan',
    templates: 'Templates',
    import: 'Import',
    importing: 'Importing...',
    completeProfileFirst: 'Complete your profile first',
    goToProfile: 'Go to Profile',
    noPlansYet: 'No lesson plans yet',
    createFirstPlan: 'Create your first lesson plan to get started',
    createLessonPlan: 'Create Lesson Plan',
    kpiTotal: 'Total Plans',
    kpiDrafts: 'Drafts',
    kpiFinalized: 'Finalized',
    kpiDownloads: 'Downloads',
    // Builder
    builderTitle: 'Lesson Plan Builder',
    basicInfo: 'Basic Information',
    class: 'Class',
    subject: 'Subject',
    topic: 'Topic / Chapter',
    board: 'Board / Curriculum',
    bookName: 'Book Name',
    selectBoard: 'Select board',
    planLanguage: 'Plan Language',
    medium: 'Medium of Instruction',
    english: 'English',
    hindi: 'Hindi',
    dateRange: 'Date Range',
    numberOfDays: 'Number of Days',
    daysHint: 'Choose either a date range OR a number of days (1–15).',
    lessonFormat: 'Lesson Format',
    useProfileDefault: 'Use profile default',
    generatePlan: 'Auto-generate Lesson Plan',
    generating: 'Generating...',
    saveDraft: 'Save Draft',
    finalize: 'Finalize & Export',
    history: 'History',
    share: 'Share',
    // Profile
    yourProfile: 'Your Profile',
    teacherName: 'Teacher Name',
    mobile: 'Mobile Number',
    schoolName: 'School Name',
    saveProfile: 'Save Profile',
    languagePreference: 'Preferred Language',
    defaultFormat: 'Default Lesson Format',
    defaultFormatHint: 'Pick your default format or upload your own. You can override per lesson plan.',
    uploadFormat: 'Upload Your Format',
    noFormat: 'System default (built-in)',
  },
  hi: {
    home: 'होम',
    library: 'लाइब्रेरी',
    analytics: 'विश्लेषण',
    profile: 'प्रोफ़ाइल',
    settings: 'सेटिंग्स',
    logout: 'लॉग आउट',
    dashboard: 'डैशबोर्ड',
    save: 'सहेजें',
    saving: 'सहेजा जा रहा है...',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    yourLessonPlans: 'आपकी पाठ योजनाएँ',
    dashboardSubtitle: 'अपनी सभी पाठ योजनाएँ बनाएँ और प्रबंधित करें',
    newLessonPlan: 'नई पाठ योजना',
    templates: 'टेम्पलेट्स',
    import: 'आयात करें',
    importing: 'आयात हो रहा है...',
    completeProfileFirst: 'पहले अपनी प्रोफ़ाइल पूरी करें',
    goToProfile: 'प्रोफ़ाइल पर जाएँ',
    noPlansYet: 'अभी कोई पाठ योजना नहीं है',
    createFirstPlan: 'शुरू करने के लिए अपनी पहली पाठ योजना बनाएँ',
    createLessonPlan: 'पाठ योजना बनाएँ',
    kpiTotal: 'कुल योजनाएँ',
    kpiDrafts: 'ड्राफ्ट',
    kpiFinalized: 'अंतिम',
    kpiDownloads: 'डाउनलोड',
    builderTitle: 'पाठ योजना निर्माता',
    basicInfo: 'मूल जानकारी',
    class: 'कक्षा',
    subject: 'विषय',
    topic: 'विषय / अध्याय',
    board: 'बोर्ड / पाठ्यक्रम',
    bookName: 'पुस्तक का नाम',
    selectBoard: 'बोर्ड चुनें',
    planLanguage: 'योजना की भाषा',
    medium: 'शिक्षा का माध्यम',
    english: 'अंग्रेज़ी',
    hindi: 'हिन्दी',
    dateRange: 'तिथि सीमा',
    numberOfDays: 'दिनों की संख्या',
    daysHint: 'तिथि सीमा या दिनों की संख्या (1–15) में से एक चुनें।',
    lessonFormat: 'पाठ प्रारूप',
    useProfileDefault: 'प्रोफ़ाइल का डिफ़ॉल्ट उपयोग करें',
    generatePlan: 'AI से पाठ योजना बनाएँ',
    generating: 'बन रही है...',
    saveDraft: 'ड्राफ्ट सहेजें',
    finalize: 'अंतिम रूप दें और निर्यात करें',
    history: 'इतिहास',
    share: 'साझा करें',
    yourProfile: 'आपकी प्रोफ़ाइल',
    teacherName: 'शिक्षक का नाम',
    mobile: 'मोबाइल नंबर',
    schoolName: 'विद्यालय का नाम',
    saveProfile: 'प्रोफ़ाइल सहेजें',
    languagePreference: 'पसंदीदा भाषा',
    defaultFormat: 'डिफ़ॉल्ट पाठ प्रारूप',
    defaultFormatHint: 'अपना डिफ़ॉल्ट प्रारूप चुनें या स्वयं अपलोड करें। हर पाठ योजना में बदल सकते हैं।',
    uploadFormat: 'अपना प्रारूप अपलोड करें',
    noFormat: 'सिस्टम डिफ़ॉल्ट (अंतर्निहित)',
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'en';
    } catch {
      return 'en';
    }
  });

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key) => (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key,
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
